import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useTenant } from '@/hooks/useTenant';

function formatGeneratedAt(value?: string): string {
  if (!value) {
    return 'N/A';
  }

  return new Date(value).toLocaleString();
}

export function AdminDashboardPage() {
  const { isAdmin, mode, activeTenantId, tenantList, setMode, setActiveTenantId, setTenantList } =
    useTenant();

  const tenantsQuery = useQuery({
    queryKey: ['admin-tenants', 'dashboard'],
    queryFn: () =>
      api.listTenants({
        page: 1,
        pageSize: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      }),
    enabled: isAdmin,
  });

  useEffect(() => {
    if (!tenantsQuery.data) {
      return;
    }

    setTenantList(tenantsQuery.data.data);
  }, [tenantsQuery.data, setTenantList]);

  const allDashboardQuery = useQuery({
    queryKey: ['admin-dashboard', 'all'],
    queryFn: () => api.getAdminDashboard(),
    enabled: isAdmin && mode === 'all',
  });

  const tenantDashboardQuery = useQuery({
    queryKey: ['admin-dashboard', 'tenant', activeTenantId],
    queryFn: () => api.getAdminTenantDashboard(activeTenantId!),
    enabled: isAdmin && mode === 'tenant' && !!activeTenantId,
  });

  const isLoading = mode === 'all' ? allDashboardQuery.isLoading : tenantDashboardQuery.isLoading;
  const error = mode === 'all' ? allDashboardQuery.error : tenantDashboardQuery.error;
  const allData = allDashboardQuery.data;
  const tenantData = tenantDashboardQuery.data;

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground">Admin access is required to view this page.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Cross-tenant metrics and tenant-level visibility
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant={mode === 'all' ? 'default' : 'outline'}
              onClick={() => {
                setMode('all');
                setActiveTenantId(null);
              }}
            >
              All tenants
            </Button>

            <div className="flex items-center gap-2">
              <Button
                variant={mode === 'tenant' ? 'default' : 'outline'}
                onClick={() => setMode('tenant')}
                disabled={tenantList.length === 0}
              >
                Selected tenant
              </Button>

              <Select
                value={activeTenantId ?? ''}
                onChange={event => setActiveTenantId(event.target.value || null)}
                disabled={mode !== 'tenant' || tenantList.length === 0}
                className="min-w-[220px]"
              >
                <option value="">Select tenant</option>
                {tenantList.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
            Loading dashboard metrics...
          </div>
        ) : error ? (
          <div className="rounded-xl border border-border bg-card p-6 text-red-600">
            {error instanceof Error ? error.message : 'Failed to load admin dashboard'}
          </div>
        ) : mode === 'all' ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard label="Tenants" value={allData?.totals.tenants ?? 0} />
              <MetricCard label="Active Tenants" value={allData?.totals.activeTenants ?? 0} />
              <MetricCard label="Users" value={allData?.totals.users ?? 0} />
              <MetricCard label="Leads" value={allData?.totals.leads ?? 0} />
            </div>

            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <h2 className="text-lg font-semibold text-foreground">Tenant Summaries</h2>
                <p className="text-sm text-muted-foreground">
                  Generated at: {formatGeneratedAt(allData?.generatedAt)}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left">
                      <th className="px-4 py-3 font-medium">Tenant</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Users</th>
                      <th className="px-4 py-3 font-medium">Leads</th>
                      <th className="px-4 py-3 font-medium">Accounts</th>
                      <th className="px-4 py-3 font-medium">Opportunities</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allData?.tenantSummaries.map(summary => (
                      <tr key={summary.tenantId} className="border-b border-border">
                        <td className="px-4 py-3 font-medium text-foreground">
                          {summary.tenantName}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {summary.isActive ? 'Active' : 'Inactive'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{summary.counts.users}</td>
                        <td className="px-4 py-3 text-muted-foreground">{summary.counts.leads}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {summary.counts.accounts}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {summary.counts.opportunities}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <h2 className="text-lg font-semibold text-foreground">Selected Tenant Metrics</h2>
            <p className="text-sm text-muted-foreground">
              {tenantData
                ? `${tenantData.tenant.name} (${tenantData.tenant.slug})`
                : 'Select a tenant to view metrics'}
            </p>
            {tenantData && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <MetricCard label="Users" value={tenantData.counts.users} />
                <MetricCard label="Leads" value={tenantData.counts.leads} />
                <MetricCard label="Accounts" value={tenantData.counts.accounts} />
                <MetricCard label="Contacts" value={tenantData.counts.contacts} />
                <MetricCard label="Opportunities" value={tenantData.counts.opportunities} />
                <MetricCard label="Audit Logs" value={tenantData.counts.auditLogs} />
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
    </div>
  );
}
